import React, { createContext, useContext, useState, useEffect, type ReactNode } from "react";
import { useNavigate, useLocation } from "react-router-dom";

interface AuthContextType {
  isLoggedIn: boolean;
  login: (token?: string) => void;
  logout: () => void;
  authChecked: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [authChecked, setAuthChecked] = useState(false);

  const isMock = import.meta.env.VITE_APP_MOCK_DATA === "true";

  /** Centralized auth check */
  const checkAuth = () => {
    const storedLoginStatus = localStorage.getItem("isLoggedIn");

    if (isMock) {
      // Mock mode: no token, just check stored login flag
      const isAuthenticated = storedLoginStatus === "true";
      setIsLoggedIn(isAuthenticated);
      setAuthChecked(true);
      return isAuthenticated;
    }

    // Real mode: require both token + login flag
    const token = localStorage.getItem("authToken");
    const isAuthenticated = !!token && storedLoginStatus === "true";

    if (!isAuthenticated) {
      localStorage.removeItem("isLoggedIn");
      localStorage.removeItem("authToken");
      localStorage.removeItem("userId");
      localStorage.removeItem("userName");
    }

    setIsLoggedIn(isAuthenticated);
    setAuthChecked(true);
    return isAuthenticated;
  };

  /** Initial check on mount */
  useEffect(() => {
    checkAuth();
  }, []);

  /** Re-check on route change */
  useEffect(() => {
    const publicPaths = [
      `${import.meta.env.VITE_APP_BASE_PATH}/sign-in`,
      `${import.meta.env.VITE_APP_BASE_PATH}/sign-up`,
      `${import.meta.env.VITE_APP_BASE_PATH}/`,
    ];

    const isProtectedRoute = !publicPaths.some((path) =>
      location.pathname.startsWith(path)
    );

    const isAuthenticated = checkAuth();

    if (!isAuthenticated && isProtectedRoute) {
      navigate(`${import.meta.env.VITE_APP_BASE_PATH}/sign-in`, { replace: true });
    }
  }, [location.pathname, navigate]);

  /** Login (mock + real) */
  const login = (token?: string) => {
    if (!isMock && token) {
      localStorage.setItem("authToken", token);
    }
    localStorage.setItem("isLoggedIn", "true");

    setIsLoggedIn(true);
    setAuthChecked(true);

    // Redirect after login (adjust target route as needed)
    navigate(`${import.meta.env.VITE_APP_BASE_PATH}/dashboard`, { replace: true });
  };

  /** Logout */
  const logout = () => {
    // ✅ CRITICAL FIX: Clear all localStorage including country data
    localStorage.clear();
    
    // ✅ Clear sessionStorage as well to reset country popup state
    sessionStorage.clear();
    
    setIsLoggedIn(false);
    navigate(`${import.meta.env.VITE_APP_BASE_PATH}/`, { replace: true });
    
    // ✅ Force reload to reset all state and show country popup
    setTimeout(() => {
    window.location.reload();
    }, 100);
  };

  return (
    <AuthContext.Provider value={{ isLoggedIn, login, logout, authChecked }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};
